import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  finalizeGrnRackScan,
  getGrnRackScanStatus,
  placeGrnSerial,
  undoGrnPlacement,
  type RackScanStatus,
} from '@/api/grn';
import { listLocations, lookupLocationByCode, type Location } from '@/api/locations';
import { ScanLine, X, AlertTriangle, Check } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/useToast';
import { useScreenWakeLock } from '@/lib/useScreenWakeLock';
import { apiError } from '@/lib/apiError';

interface Props {
  open: boolean;
  grnId: number;
  grnNo: string;
  onClose: () => void;
  onVerified: () => void;
}

/**
 * Rack-based stock entry scan.
 *
 * Flow:
 *  1. Stock person scans a Location QR — becomes the "current location"
 *  2. For each unit in that location, scan the unit's Serial QR
 *  3. Each serial gets bound to the current location and flipped to InWarehouse
 *  4. To change location, scan a different Location QR
 *  5. GRN auto-verifies when the last Pending serial is placed
 */
export function GrnScanModal({ open, grnId, grnNo, onClose, onVerified }: Props) {
  const qc = useQueryClient();
  const toast = useToast();

  const [locationCode, setLocationCode] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [locationName, setLocationName] = useState<string | null>(null);
  const [locationLevel, setLocationLevel] = useState<string | null>(null);
  const [serialInput, setSerialInput] = useState('');

  const locationRef = useRef<HTMLInputElement>(null);
  const serialRef = useRef<HTMLInputElement>(null);
  // Hardware barcode scanners (Urovo CT-58S trigger, USB guns) can fire
  // faster than the backend can save. Serials typed while a save is in
  // flight go here and drain as soon as the previous mutation settles —
  // no dropped scans.
  const serialQueueRef = useRef<string[]>([]);

  useScreenWakeLock(open);

  const statusQuery = useQuery({
    queryKey: ['grn-rack-status', grnId],
    queryFn: () => getGrnRackScanStatus(grnId),
    enabled: open && grnId > 0,
    refetchOnWindowFocus: false,
  });
  const status: RackScanStatus | undefined = statusQuery.data;

  // All active locations for the autocomplete dropdown on the Location input.
  const locationsQuery = useQuery({
    queryKey: ['locations-flat'],
    queryFn: () => listLocations({}),
    enabled: open,
    staleTime: 60_000,
  });
  const allLocations: Location[] = locationsQuery.data ?? [];

  // Filter suggestions as user types. Substring match on code or name, case-insensitive.
  const suggestions = useMemo(() => {
    const q = locationInput.trim().toLowerCase();
    if (!q) return [];
    return allLocations
      .filter((l) => l.code.toLowerCase().includes(q) || l.name.toLowerCase().includes(q))
      .slice(0, 10);
  }, [locationInput, allLocations]);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);

  useEffect(() => {
    setHighlightIdx(0);
  }, [suggestions]);

  // Auto-focus: location input first, then serial once location is set
  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      if (!locationCode) locationRef.current?.focus();
      else serialRef.current?.focus();
    }, 60);
  }, [open, locationCode]);

  // Reset local state when modal closes
  useEffect(() => {
    if (!open) {
      setLocationCode('');
      setLocationInput('');
      setLocationName(null);
      setLocationLevel(null);
      setSerialInput('');
    }
  }, [open]);

  const placeMutation = useMutation({
    mutationFn: ({ code, serial }: { code: string; serial: string }) =>
      placeGrnSerial(grnId, code, serial),
    onSuccess: async (res) => {
      // Cache location details from server response (in case the location was a fresh scan)
      setLocationCode(res.location.code);
      setLocationName(res.location.name);
      setLocationLevel(res.location.level);
      await qc.invalidateQueries({ queryKey: ['grn-rack-status', grnId] });
      setTimeout(() => serialRef.current?.focus(), 20);
      if (res.allPlaced) {
        toast.push('All units placed — review and click Save to confirm stock entry.', 'success');
      }
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Scan failed'), 'error');
      setTimeout(() => serialRef.current?.focus(), 20);
    },
    onSettled: () => {
      // Drain the next queued serial (if any) as soon as this one finishes.
      const next = serialQueueRef.current.shift();
      if (next && locationCodeRef.current) {
        placeMutation.mutate({ code: locationCodeRef.current, serial: next });
      }
    },
  });

  // Latest locationCode in a ref so the mutation's onSettled drainer can see
  // the current value without becoming stale when a rapid scan burst clears
  // and re-sets state.
  const locationCodeRef = useRef(locationCode);
  locationCodeRef.current = locationCode;

  function submitSerial(serial: string) {
    if (!serial || !locationCode) return;
    if (placeMutation.isPending) {
      serialQueueRef.current.push(serial);
    } else {
      placeMutation.mutate({ code: locationCode, serial });
    }
  }

  const undoMutation = useMutation({
    mutationFn: (serialId: number) => undoGrnPlacement(grnId, serialId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grn-rack-status', grnId] });
      qc.invalidateQueries({ queryKey: ['grn'] });
      toast.push('Placement undone', 'info');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Undo failed'), 'error'),
  });

  const locationMutation = useMutation({
    mutationFn: (code: string) => lookupLocationByCode(code),
    onSuccess: (loc) => {
      setLocationCode(loc.code);
      setLocationName(loc.name);
      setLocationLevel(loc.level);
      setLocationInput('');
      setTimeout(() => serialRef.current?.focus(), 20);
      toast.push(`Location set: ${loc.code} — ${loc.name}`, 'success');
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Invalid location'), 'error');
      setTimeout(() => locationRef.current?.focus(), 20);
    },
  });

  const finalizeMutation = useMutation({
    mutationFn: () => finalizeGrnRackScan(grnId),
    onSuccess: () => {
      // Save + auto-confirm bumps warehouse stock, so refresh every dependent view.
      qc.invalidateQueries({ queryKey: ['grn-rack-status', grnId] });
      qc.invalidateQueries({ queryKey: ['grn'] });
      qc.invalidateQueries({ queryKey: ['grn-pending-stock-entry'] });
      qc.invalidateQueries({ queryKey: ['wh-stock'] });
      qc.invalidateQueries({ queryKey: ['wh-serials'] });
      qc.invalidateQueries({ queryKey: ['location-occupancy'] });
      qc.invalidateQueries({ queryKey: ['po'] });
      toast.pushStep(`${grnNo} — scan saved & stock confirmed`, {
        body: 'Every unit is placed. Warehouse stock and ledger are updated.',
        href: '/inventory/wh-stock',
        nextLabel: 'View Warehouse Stock',
        tag: `rack-scan-${grnId}`,
      });
      onVerified();
      onClose();
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Save failed'), 'error'),
  });

  function handleLocationSubmit(e: React.FormEvent) {
    e.preventDefault();
    // If the dropdown is open with a highlighted suggestion, pick it instead
    // of round-tripping to the server (feels instant + saves one lookup).
    if (showSuggestions && suggestions.length > 0) {
      const pick = suggestions[Math.min(highlightIdx, suggestions.length - 1)];
      selectSuggestion(pick);
      return;
    }
    const v = locationInput.trim();
    if (!v) return;
    locationMutation.mutate(v);
  }

  function selectSuggestion(loc: Location) {
    setShowSuggestions(false);
    setLocationCode(loc.code);
    setLocationName(loc.name);
    setLocationLevel(loc.level);
    setLocationInput('');
    setTimeout(() => serialRef.current?.focus(), 20);
    toast.push(`Location set: ${loc.code} — ${loc.name}`, 'success');
  }

  function handleLocationKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }

  function handleSerialSubmit(e: React.FormEvent) {
    e.preventDefault();
    const s = serialInput.trim();
    if (!s) return;
    if (!locationCode) {
      toast.push('Scan a location first', 'error');
      setTimeout(() => locationRef.current?.focus(), 20);
      return;
    }
    // Clear synchronously so the next scan can type into a fresh input
    // even if this one is still round-tripping to the backend.
    setSerialInput('');
    submitSerial(s);
  }

  function clearLocation() {
    setLocationCode('');
    setLocationName(null);
    setLocationLevel(null);
    setTimeout(() => locationRef.current?.focus(), 20);
  }

  const allPlaced = status ? status.totalPending === 0 && status.totalPlaced > 0 : false;

  const recentPlacements = useMemo(() => status?.placements.slice(0, 20) ?? [], [status?.placements]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="relative flex w-full max-w-4xl flex-col rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-2xl"
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--border-color)] px-6 py-4">
          <div>
            <div className="text-lg font-extrabold text-accent">QR / Barcode Scan — {grnNo}</div>
            <div className="text-xs text-[var(--text-muted)]">
              Scan a Location QR, then each unit's Serial QR. Every serial gets bound to the current location.
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {statusQuery.isLoading ? (
            <div className="py-8 text-center text-sm text-[var(--text-muted)]">Loading…</div>
          ) : !status ? (
            <div className="py-8 text-center text-sm text-danger">Failed to load scan status</div>
          ) : status.totalSerials === 0 ? (
            <div className="rounded-lg border border-warning/30 bg-warning/10 p-4 text-center text-sm">
              <div className="mb-1 text-2xl"><AlertTriangle size={26} className="mx-auto text-warning" /></div>
              <div className="font-bold text-warning">No serials generated for this GRN.</div>
              <div className="mt-1 text-[var(--text-muted)]">
                This is likely an older GRN created before the barcode-generation feature.
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Progress summary */}
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--border-color)] bg-white/[0.03] px-4 py-3">
                <div className="text-sm">
                  <span className="text-[var(--text-muted)]">Placed: </span>
                  <strong className={allPlaced ? 'text-success' : 'text-accent'}>
                    {status.totalPlaced} / {status.totalSerials}
                  </strong>
                </div>
                {status.scanStatus !== 'Pending' && (
                  <Badge tone={status.scanStatus === 'Verified' ? 'success' : 'danger'}>
                    {status.scanStatus}
                  </Badge>
                )}
                {allPlaced && status.scanStatus !== 'Verified' && (
                  <div className="text-xs font-bold text-success">
                    <Check size={13} className="mr-1 inline-block align-[-2px]" /> All units placed — click <span className="underline">Save</span> to confirm stock entry
                  </div>
                )}
                {status.scanStatus === 'Verified' && (
                  <div className="text-xs font-bold text-success">
                    <Check size={13} className="mr-1 inline-block align-[-2px]" /> Saved — Stock Entry can now be confirmed on the GRN row
                  </div>
                )}
              </div>

              {/* Current location pill */}
              <div className="rounded-lg border border-[var(--border-color)] bg-[var(--input-bg)] p-3">
                <div className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--text-muted)]">
                  Current Location
                </div>
                {locationCode ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {locationLevel && (
                      <span className="rounded border border-emerald-500/30 bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-emerald-400">
                        {locationLevel}
                      </span>
                    )}
                    <span className="font-mono text-base font-black text-[var(--text-primary)]">
                      {locationCode}
                    </span>
                    {locationName && (
                      <span className="text-xs text-[var(--text-muted)]">— {locationName}</span>
                    )}
                    <button
                      onClick={clearLocation}
                      className="ml-auto text-[11px] font-bold text-[var(--text-muted)] hover:text-danger"
                    >
                      Change location
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleLocationSubmit} className="relative flex gap-2">
                    <div className="relative flex-1">
                      {/* Passive "scan target" badge — visual hint that this
                          field expects a scanner trigger, not a click. */}
                      <span
                        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded bg-accent px-1.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-sm sm:px-2"
                        aria-hidden="true"
                      >
                        <ScanLine size={14} strokeWidth={3} />
                        <span className="hidden sm:inline">Scan</span>
                      </span>
                      <input
                        ref={locationRef}
                        autoFocus
                        type="text"
                        autoComplete="off"
                        className="w-full rounded-lg border-2 border-[var(--border-color)] bg-[var(--input-bg)] py-2 pl-9 pr-3 font-mono text-sm text-[var(--text-primary)] transition-all focus:border-accent focus:outline-none focus:ring-4 focus:ring-accent/25 disabled:opacity-50 sm:pl-[5.5rem]"
                        placeholder="Pull trigger to scan Location QR…"
                        value={locationInput}
                        onChange={(e) => { setLocationInput(e.target.value); setShowSuggestions(true); }}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                        onKeyDown={handleLocationKeyDown}
                        disabled={locationMutation.isPending}
                      />
                      {/* Autocomplete dropdown */}
                      {showSuggestions && suggestions.length > 0 && (
                        <div
                          role="listbox"
                          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-lg"
                        >
                          {suggestions.map((loc, i) => (
                            <button
                              key={loc.id}
                              type="button"
                              role="option"
                              aria-selected={i === highlightIdx}
                              onMouseEnter={() => setHighlightIdx(i)}
                              // onMouseDown (not onClick) so it fires before the input's onBlur closes the panel
                              onMouseDown={(e) => { e.preventDefault(); selectSuggestion(loc); }}
                              className={
                                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition ' +
                                (i === highlightIdx
                                  ? 'bg-accent/15 text-[var(--text-primary)]'
                                  : 'text-[var(--text-secondary)] hover:bg-white/[0.04]')
                              }
                            >
                              <span className="rounded border border-emerald-500/30 bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-emerald-400">
                                {loc.level}
                              </span>
                              <span className="font-mono font-bold">{loc.code}</span>
                              <span className="truncate text-[var(--text-muted)]">— {loc.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      type="submit"
                      disabled={!locationInput.trim() || locationMutation.isPending}
                    >
                      {locationMutation.isPending ? 'Checking…' : 'Set Location'}
                    </Button>
                  </form>
                )}
              </div>

              {/* Serial scan input */}
              <div className="rounded-lg border border-[var(--border-color)] bg-[var(--input-bg)] p-3">
                <div className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--text-muted)]">
                  Scan Items
                </div>
                <form onSubmit={handleSerialSubmit} className="flex gap-2">
                  <div className="relative flex-1">
                    <span
                      className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded bg-accent px-1.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-sm sm:px-2"
                      aria-hidden="true"
                    >
                      <ScanLine size={14} strokeWidth={3} />
                      <span className="hidden sm:inline">Scan</span>
                    </span>
                    <input
                      ref={serialRef}
                      type="text"
                      className="w-full rounded-lg border-2 border-[var(--border-color)] bg-[var(--input-bg)] py-2 pl-9 pr-3 font-mono text-sm text-[var(--text-primary)] transition-all focus:border-accent focus:outline-none focus:ring-4 focus:ring-accent/25 disabled:opacity-50 sm:pl-[5.5rem]"
                      placeholder={locationCode ? 'Pull trigger…' : 'Set a location first'}
                      value={serialInput}
                      onChange={(e) => setSerialInput(e.target.value)}
                      disabled={!locationCode}
                    />
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    type="submit"
                    disabled={!serialInput.trim() || !locationCode}
                    title="Place the typed serial (same as pressing Enter)"
                  >
                    + Place
                  </Button>
                </form>
              </div>

              {/* Per-item progress */}
              <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-card)] p-3">
                <div className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--text-muted)]">
                  Items
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase text-[var(--text-muted)]">
                        <th className="pb-1">Code</th>
                        <th className="pb-1">Name</th>
                        <th className="pb-1 text-right">Placed</th>
                        <th className="pb-1 text-right">Pending</th>
                        <th className="pb-1 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {status.items.map((it) => (
                        <tr key={it.grnItemId} className="border-t border-[var(--border-color)]">
                          <td className="py-1.5 font-mono">{it.itemCode}</td>
                          <td className="py-1.5">{it.itemName}</td>
                          <td className={'py-1.5 text-right font-bold ' + (it.placed === it.total ? 'text-success' : 'text-accent')}>
                            {it.placed}
                          </td>
                          <td className="py-1.5 text-right text-[var(--text-muted)]">{it.pending}</td>
                          <td className="py-1.5 text-right">{it.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recent placements */}
              {recentPlacements.length > 0 && (
                <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-card)] p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--text-muted)]">
                      Recent placements ({status.totalPlaced} total)
                    </div>
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[var(--bg-card)]">
                        <tr className="text-left text-[10px] uppercase text-[var(--text-muted)]">
                          <th className="pb-1">Serial</th>
                          <th className="pb-1">Location</th>
                          <th className="pb-1">Scanned</th>
                          <th className="pb-1 text-right">Undo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentPlacements.map((p) => (
                          <tr key={p.serialId} className="border-t border-[var(--border-color)]">
                            <td className="py-1.5 font-mono">{p.serialNo}</td>
                            <td className="py-1.5 font-mono">{p.locationCode}</td>
                            <td className="py-1.5 text-[var(--text-muted)]">
                              {p.scannedAt ? new Date(p.scannedAt).toLocaleTimeString() : '—'}
                            </td>
                            <td className="py-1.5 text-right">
                              <button
                                onClick={() => undoMutation.mutate(p.serialId)}
                                disabled={undoMutation.isPending}
                                className="text-[11px] font-bold text-danger hover:underline disabled:opacity-50"
                                title="Undo this placement"
                              >
                                undo
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-[var(--border-color)] px-6 py-3">
          <div className="text-[11px] text-[var(--text-muted)]">
            {status && status.scanStatus !== 'Verified' && !allPlaced && status.totalSerials > 0 && (
              <>Place all {status.totalSerials} units, then click Save.</>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="glass" size="sm" onClick={onClose}>
              Close
            </Button>
            {status && status.totalSerials > 0 && status.scanStatus !== 'Verified' && (
              <Button
                variant="success"
                size="sm"
                onClick={() => finalizeMutation.mutate()}
                disabled={!allPlaced || finalizeMutation.isPending}
                title={allPlaced ? 'Save scan and post to warehouse stock' : 'Place all units first'}
              >
                {finalizeMutation.isPending ? (
                  'Saving…'
                ) : (
                  <>
                    <span className="sm:hidden"><Check size={14} className="mr-1 inline-block align-[-2px]" /> Save & Confirm</span>
                    <span className="hidden sm:inline"><Check size={14} className="mr-1 inline-block align-[-2px]" /> Save & Confirm Stock Entry</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
